import { Inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {
  ClientListItem,
  ClientSettings,
  CountyOverrideRule,
  NotificationSettings,
  Organization,
  Results,
  Tag
} from '@ee/common/models';
import { IServerSideGetRowsRequest } from 'ag-grid-enterprise';
import { AccountType } from '@ee/common/enums';
import { fallbackPlaintiffOptions, normalizePlaintiffNames, PlaintiffOptions } from '@ee/common/utils';
import { IdentifierService } from './identifier.service';

@Injectable({ providedIn: 'root' })
export class ClientService implements IdentifierService {
  constructor(private http: HttpClient, @Inject('environment') private environment) {
  }

  addNewClient(client: Organization): Observable<Organization> {
    return this.http.post<Organization>(`${this.environment.api_prefix}api/clients`, client);
  }

  updateClient(organization: Organization): Observable<Organization> {
    return this.http.put<Organization>(`${this.environment.api_prefix}api/clients`, organization);
  }

  updateQuickbooksCustomerId(clientId: string, customer: {id: string, display_name: string} | null): Observable<boolean> {
    return this.http.put<boolean>(`${this.environment.api_prefix}api/plugins/invoice/quickbooks/clients/${clientId}/link/customer`, customer);
  }

  /**
   * Slim clients list from `POST /api/clients/search` (grid + autocomplete).
   * Prefer this over [findClients] for CLIENT account type.
   */
  findClientList(request: IServerSideGetRowsRequest): Observable<Results<ClientListItem>> {
    return this.http.post<Results<ClientListItem>>(
      `${this.environment.api_prefix}api/clients/search`, request);
  }

  /**
   * Property managers: full Organization from property-managers/search.
   * Clients: same as [findClientList] (slim [ClientListItem] — not full Organization).
   */
  findClients(request: IServerSideGetRowsRequest, accountType: AccountType.CLIENT): Observable<Results<ClientListItem>>;
  findClients(request: IServerSideGetRowsRequest, accountType: AccountType.PROPERTY_MANAGER): Observable<Results<Organization>>;
  findClients(request: IServerSideGetRowsRequest, accountType?: AccountType): Observable<Results<ClientListItem | Organization>>;
  findClients(request: IServerSideGetRowsRequest, accountType: AccountType = AccountType.CLIENT):
    Observable<Results<ClientListItem | Organization>> {
    if (accountType === AccountType.PROPERTY_MANAGER) {
      return this.http.post<Results<Organization>>(
        `${this.environment.api_prefix}api/property-managers/search`, request);
    }
    return this.findClientList(request);
  }

  /**
   * Finds archived clients for the logged-in attorney.
   * @param request The server-side request parameters for pagination, sorting, and filtering
   * @returns Observable of Results containing archived Organization records
   */
  findArchivedClients(request: IServerSideGetRowsRequest): Observable<Results<Organization>> {
    return this.http.post<Results<Organization>>(
      `${this.environment.api_prefix}api/clients/archived`, request);
  }

  searchClientsByCompanyName(query: string, fields: string[] = [], accountType: AccountType = AccountType.CLIENT):
    Observable<Organization[]> {
    const params = {};
    if (query) {
      params['q'] = encodeURIComponent(query);
    }
    if (fields?.length) {
      params['f'] = fields.join(',');
    }
    if (accountType === AccountType.PROPERTY_MANAGER) {
      params['account-type'] = AccountType.PROPERTY_MANAGER;
    }

    return this.http.get<Organization[]>(`${this.environment.api_prefix}api/clients/search`, { params });
  }

  getClientByIdentifier(identifier: string, fields: string[] = []): Observable<Organization | undefined> {
    const params = {};
    if (fields?.length) {
      params['f'] = fields.join(',');
    }
    return this.http.get<Organization | undefined>(
      this.environment.api_prefix + `api/clients/${identifier}`, { params });
  }

  getClient(id: string): Observable<Organization> {
    return this.http.get<Organization>(`${this.environment.api_prefix}api/clients/${id}?type=id`);
  }

  getClientNotes(clientId: string): Observable<string | undefined> {
    return this.http.get(this.environment.api_prefix + `api/clients/${clientId}/notes`,
      {responseType: 'text'});
  }

  updateClientNotes(clientId: string, notes: string): Observable<boolean> {
    return this.http.put<boolean>(this.environment.api_prefix + `api/clients/${clientId}/notes`, notes);
  }

  updateClientTags(id: string, tags: Tag[], useId: boolean = false): Observable<boolean> {
    const tagIdentifiers = tags.map(t => t.value);
    const url = useId 
      ? `${this.environment.api_prefix}api/clients/${id}/tags?type=id`
      : `${this.environment.api_prefix}api/clients/${id}/tags`;
    return this.http.put<boolean>(url, tagIdentifiers);
  }

  updateClientLocationIds(clientId: string, locationIds: string[]): Observable<boolean> {
    return this.http.put<boolean>(`${this.environment.api_prefix}api/clients/${clientId}/locations`, locationIds);
  }

  generateClientExport(): Observable<any> {
    return this.http.get(`${this.environment.api_prefix}api/clients/export-client-list`, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  deleteClientById(id: string): Observable<boolean> {
    return this.http.delete<boolean>(`${this.environment.api_prefix}api/clients/identifier/${id}`);
  }

  /**
   * Archives a client organization by identifier.
   * @param identifier The client identifier (e.g., "ABC123")
   * @param removeUsers Whether to remove all user associations from the archived account
   * @returns Observable of the archived Organization
   */
  archiveClient(identifier: string, removeUsers: boolean): Observable<Organization> {
    return this.http.post<Organization>(
      `${this.environment.api_prefix}api/clients/identifier/${identifier}/archive`,
      { removeUsers }
    );
  }

  /**
   * Unarchives a previously-archived client by database id (EE-205). Keyed by id rather than
   * identifier because an identifier isn't guaranteed unique across archived + active clients.
   *
   * @param id The client organization's database id
   * @returns Observable of the unarchived Organization
   */
  unarchiveClient(id: string): Observable<Organization> {
    return this.http.post<Organization>(
      `${this.environment.api_prefix}api/clients/${id}/unarchive`,
      {}
    );
  }

  checkIdentifierAvailability(identifier: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.environment.api_prefix}api/organizations/check-identifier/${identifier}`);
  }

  updateNotificationSettings(clientId: string, settings: NotificationSettings): Observable<NotificationSettings> {
    return this.http.put<NotificationSettings>(
      `${this.environment.api_prefix}api/clients/${clientId}/notification-settings`, settings);
  }

  getClientSettings(clientId: string | null): Observable<ClientSettings | null> {
    if (!clientId || clientId === 'new') {
      return of(null);
    }
    return this.http.get<ClientSettings | null>(`${this.environment.api_prefix}api/clients/${clientId}/settings`);
  }

  updateClientSetting(clientId: string, setting: string, value: any): Observable<ClientSettings> {
    const requestBody = {};
    requestBody[setting] = value;
    return this.http.put<ClientSettings>(`${this.environment.api_prefix}api/clients/${clientId}/settings`, requestBody);
  }

  addClientCountyOverrideRule(clientId: string, countyOverrideRule: CountyOverrideRule) {
    return this.http.put<boolean>(`${this.environment.api_prefix}api/clients/${clientId}/county`, countyOverrideRule);
  }

  removeClientCountyOverrideRule(clientId: string, countyId: string) {
    return this.http.delete<boolean>(`${this.environment.api_prefix}api/clients/${clientId}/county/${countyId}`);
  }

  updatePlaintiffHistory(clientId: string, attorneyId: string, plaintiffHistory: string[]): Observable<string[]> {
    return this.http.put<string[]>(`${this.environment.api_prefix}api/clients/${clientId}/attorney/${attorneyId}/plaintiffs`, plaintiffHistory);
  }

  /**
   * The client account's plaintiff options and field behaviour (EE-367).
   *
   * A failed request falls back to today's field — free text, the company name offered and prefilled —
   * so an outage never blocks opening a case. `null` when there is no client yet.
   */
  async getPlaintiffOptions(
    clientId: string | null | undefined,
    attorneyId: string | null | undefined,
    companyName: string | null | undefined
  ): Promise<PlaintiffOptions | null> {
    if (!clientId || !attorneyId) {
      return null;
    }
    try {
      const result = await firstValueFrom(this.http.get<PlaintiffOptions>(
        `${this.environment.api_prefix}api/clients/${clientId}/attorney/${attorneyId}/plaintiff-options`));
      return { ...result, options: normalizePlaintiffNames(result.options) };
    } catch (e) {
      console.error(e);
      return fallbackPlaintiffOptions(companyName);
    }
  }

  /** The client account's saved plaintiff list, as edited in its Settings tab. */
  async getPlaintiffHistory(clientId: string, attorneyId: string): Promise<string[]> {
    if (!clientId) {
      return [];
    }
    try {
      return await firstValueFrom(this.http.get<string[]>(
        `${this.environment.api_prefix}api/clients/${clientId}/attorney/${attorneyId}/plaintiffs`));
    } catch (e) {
      console.error(e);
      return [];
    }
  }
}
